
import { Data, EventHandler, Util } from 'nj-utils';
import { coreConfig, eventsName } from 'nj-components/core.config';
import AbstractComponent from 'nj-components/AbstractComponent';

const NAME                = `${coreConfig.KEY_PREFIX}-modal`;
const DATA_KEY            = `${coreConfig.KEY_PREFIX}.modal`;
const EVENT_KEY           = `.${DATA_KEY}`;
const DATA_API_KEY        = coreConfig.KEY_PREFIX;
const ESCAPE_KEYCODE      = 27; // KeyboardEvent.which value for Escape (Esc) key

const CLASSNAME = {
  backdrop    : `${coreConfig.KEY_PREFIX}-modal__backdrop`,
  fade        : 'fade',
  show        : 'show'
};

const SELECTOR = {
  default     : `.${NAME}`,
  dataDismiss : '[data-dismiss="modal"]',
  dataToggle  : '[data-toggle="modal"]',
  modalBody   : `.${coreConfig.KEY_PREFIX}-modal__body`,
  dialog      : `.${coreConfig.KEY_PREFIX}-modal__dialog`
};

export const EVENT = {
  show           : `${eventsName.show}${EVENT_KEY}`,
  shown          : `${eventsName.shown}${EVENT_KEY}`,
  focusin        : `${eventsName.focusin}${EVENT_KEY}`,
  hide           : `${eventsName.hide}${EVENT_KEY}`,
  hidden         : `${eventsName.hidden}${EVENT_KEY}`,
  keydownDismiss : `${eventsName.keydown}.dismiss${EVENT_KEY}`,
  clickDismiss   : `${eventsName.click}.dismiss${EVENT_KEY}`,
  clickDataApi   : `${eventsName.click}${EVENT_KEY}${DATA_API_KEY}`,
  mouseupDismiss : `${eventsName.mouseup}.dismiss${EVENT_KEY}`,
  mousedownDismiss : `${eventsName.mousedown}.dismiss${EVENT_KEY}`
};

class Modal extends AbstractComponent {
  constructor(element) {
    super(Modal, element);

    this.element = element; // The modal, from a DOM perspective

    this._backdrop            = null;
    this._ignoreBackdropClick = null;
    this._isShown             = false;
    this._isTransitioning     = false;

    // It's necessary to implement line below to enable data api
    if (this.element) {
      this._dialog = this.element.querySelector(SELECTOR.dialog);
      Data.setData(element, DATA_KEY, this);
    }
  }

  static init(options) {
    return super.init(this, options, SELECTOR.default);
  }

  static getInstance(element) {
    return Data.getData(element, DATA_KEY);
  }

  _enforceFocus() {
    EventHandler.off(document, EVENT.focusin);  // Guard against infinite focus loop

    EventHandler.on(document, EVENT.focusin, (event) => {
      if (document !== event.target &&
          this.element !== event.target &&
          !this.element.contains(event.target)) {
        this.element.focus();
      }
    });
  }

  _hideModal() {
    this.element.style.display = 'none';
    this.element.setAttribute('aria-hidden', true);
    this.element.removeAttribute('aria-modal');
    this._isTransitioning = false;
    this._showBackdrop();
  }

  _removeBackdrop() {
    if (this._backdrop) {
      this._backdrop.parentNode.removeChild(this._backdrop);
      this._backdrop = null;
    }
  }

  _setEscapeEvent() {
    if (this._isShown) {
      EventHandler.on(this.element, EVENT.keydownDismiss, (event) => {
        if (event.which === ESCAPE_KEYCODE) {
          event.preventDefault();
          this.hide();
        }
      });
    } else {
      EventHandler.off(this.element, EVENT.keydownDismiss);
    }
  }

  _showBackdrop(callback) {
    const animate = this.element.classList.contains(CLASSNAME.fade)
      ? CLASSNAME.fade : '';

    if (this._isShown) {
      this._backdrop = document.createElement('div');
      this._backdrop.className = CLASSNAME.backdrop;

      if (animate) {
        this._backdrop.classList.add(animate);
      }

      document.body.appendChild(this._backdrop);

      EventHandler.on(this.element, EVENT.clickDismiss, (event) => {
        if (this._ignoreBackdropClick) {
          this._ignoreBackdropClick = false;
          return;
        }
        if (event.target !== event.currentTarget) {
          return;
        }
        this.hide();
      });

      if (animate) {
        Util.reflow(this._backdrop);
      }

      this._backdrop.classList.add(CLASSNAME.show);

      if (!callback) {
        return;
      }

      if (!animate) {
        callback();
        return;
      }

      const backdropTransitionDuration = Util.getTransitionDurationFromElement(this._backdrop);

      EventHandler.one(this._backdrop, Util.TRANSITION_END, callback);
      Util.emulateTransitionEnd(this._backdrop, backdropTransitionDuration);
    } else if (!this._isShown && this._backdrop) {
      this._backdrop.classList.remove(CLASSNAME.show);

      const callbackRemove = () => {
        this._removeBackdrop();
        if (callback) {
          callback();
        }
      };

      if (this.element.classList.contains(CLASSNAME.fade)) {
        const backdropTransitionDuration = Util.getTransitionDurationFromElement(this._backdrop);

        EventHandler.one(this._backdrop, Util.TRANSITION_END, callbackRemove);
        Util.emulateTransitionEnd(this._backdrop, backdropTransitionDuration);
      } else {
        callbackRemove();
      }
    } else if (callback) {
      callback();
    }
  }

  _showElement() {
    const transition = this.element.classList.contains(CLASSNAME.fade);

    if (!this.element.parentNode ||
        this.element.parentNode.nodeType !== Node.ELEMENT_NODE) {
      // Don't move modal's DOM position
      document.body.appendChild(this.element);
    }

    this.element.style.display = 'block';
    this.element.removeAttribute('aria-hidden');
    this.element.setAttribute('aria-modal', true);

    this.element.scrollTop = 0;

    if (transition) {
      Util.reflow(this.element);
    }

    this.element.classList.add(CLASSNAME.show);

    this._enforceFocus();

    const transitionComplete = () => {
      this.element.focus();
      this._isTransitioning = false;
    };

    if (transition) {
      const transitionDuration  = Util.getTransitionDurationFromElement(this._dialog);

      EventHandler.one(this._dialog, Util.TRANSITION_END, transitionComplete);
      Util.emulateTransitionEnd(this._dialog, transitionDuration);
    } else {
      transitionComplete();
    }
  }

  dispose() {
    Data.removeData(this.element, DATA_KEY);

    /**
     * `document` has 2 events `Event.FOCUSIN` and `Event.CLICK_DATA_API`
     * Do not move `document` in `htmlElements` array
     * It will remove `Event.CLICK_DATA_API` event that should remain
     */
    EventHandler.off(document, EVENT.focusin);

    this.element              = null;
    this._dialog              = null;
    this._backdrop            = null;
    this._isShown             = null;
    this._ignoreBackdropClick = null;
    this._isTransitioning     = null;
  }

  hide(event) {
    if (event) {
      event.preventDefault();
    }

    if (!this._isShown || this._isTransitioning) {
      return;
    }

    this._isShown = false;
    const transition = this.element.classList.contains(CLASSNAME.fade);

    if (transition) {
      this._isTransitioning = true;
    }

    this._setEscapeEvent();

    EventHandler.off(document, EVENT.focusin);

    this.element.classList.remove(CLASSNAME.show);

    EventHandler.off(this.element, EVENT.clickDismiss);
    EventHandler.off(this._dialog, EVENT.mousedownDismiss);

    if (transition) {
      const transitionDuration  = Util.getTransitionDurationFromElement(this.element);

      EventHandler.one(this.element, Util.TRANSITION_END, (event) => this._hideModal(event));
      Util.emulateTransitionEnd(this.element, transitionDuration);
    } else {
      this._hideModal();
    }
  }

  show() {
    if (this._isShown || this._isTransitioning) {
      return;
    }

    if (this.element.classList.contains(CLASSNAME.fade)) {
      this._isTransitioning = true;
    }

    this._isShown = true;

    this._setEscapeEvent();

    EventHandler.on(this.element, EVENT.clickDismiss, SELECTOR.dataDismiss, (event) => this.hide(event));

    EventHandler.on(this._dialog, EVENT.mousedownDismiss, () => {
      EventHandler.one(this.element, EVENT.mouseupDismiss, (event) => {
        if (event.target.isEqualNode(this.element)) {
          this._ignoreBackdropClick = true;
        }
      });
    });

    this._showBackdrop(() => this._showElement());
  }

  toggle() {
    return this._isShown ? this.hide() : this.show();
  }

  static selector = SELECTOR.default;
}


/**
 * ------------------------------------------------------------------------
 * Data Api implementation
 * ------------------------------------------------------------------------
 */
EventHandler.on(document, EVENT.clickDataApi, SELECTOR.dataToggle, (event) => {
  let target;
  const selector = Util.getSelectorFromElement(event.target);

  if (selector) {
    target = document.querySelector(selector);
  }

  if (event.currentTarget.tagName === 'A' || event.currentTarget.tagName === 'AREA') {
    event.preventDefault();
  }

  const component = Modal.getInstance(target);
  if (component) {
    component.toggle();
  }
});

export default Modal;
